import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { SETTINGS_SEARCH_INDEX } from '../pages/Settings/config/settingsSearchIndex';

export interface CommandResult {
  id: string;
  type: 'navigation' | 'team_member' | 'invoice' | 'job' | 'booking' | 'action' | 'settings';
  title: string;
  subtitle?: string;
  icon: string;
  route?: string;
  action?: string;
}

const NAV_ITEMS: CommandResult[] = [
  { id: 'nav-dashboard', type: 'navigation', title: 'Today', icon: '\u229E', route: '/today' },
  { id: 'nav-schedule', type: 'navigation', title: 'Schedule', icon: '\u25EB', route: '/schedule' },
  { id: 'nav-team', type: 'navigation', title: 'Team & Payroll', icon: '\u25C9', route: '/team' },
  { id: 'nav-financials', type: 'navigation', title: 'Financials', icon: '\u25A4', route: '/money' },
  { id: 'nav-money-outstanding', type: 'navigation', title: 'Outstanding', icon: '\u23F1', route: '/money/outstanding' },
  { id: 'nav-money-invoices', type: 'navigation', title: 'Invoices', icon: '\uD83D\uDCC4', route: '/money/invoices' },
  { id: 'nav-money-expenses', type: 'navigation', title: 'Expenses', icon: '\uD83E\uDDFE', route: '/money/expenses' },
  { id: 'nav-money-recurring', type: 'navigation', title: 'Recurring', icon: '\u21BB', route: '/money/recurring' },
  { id: 'nav-money-revenue', type: 'navigation', title: 'Revenue', icon: '\uD83D\uDCC8', route: '/money/revenue' },
  { id: 'nav-money-tax', type: 'navigation', title: 'Tax', icon: '\uD83D\uDCB5', route: '/money/tax' },
  { id: 'nav-money-disputes', type: 'navigation', title: 'Disputes', icon: '\u26A0', route: '/money/disputes' },
  { id: 'nav-reports', type: 'navigation', title: 'Reports', icon: '\uD83D\uDCCA', route: '/reports' },
  { id: 'nav-settings', type: 'navigation', title: 'Settings', icon: '\u2699', route: '/settings' },
];

const ACTION_ITEMS: CommandResult[] = [
  { id: 'act-schedule', type: 'action', title: 'Add to Schedule', icon: '+', route: '/schedule', action: 'add-to-schedule' },
  { id: 'act-invoice', type: 'action', title: 'New Invoice', icon: '+', route: '/money/invoices', action: 'new-invoice' },
  { id: 'act-team', type: 'action', title: 'Add to Team', icon: '+', route: '/team', action: 'add-to-team' },
];

interface UseCommandPaletteResult {
  query: string;
  setQuery: (q: string) => void;
  results: CommandResult[];
  loading: boolean;
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
}

export function useCommandPalette(businessId: string | undefined, isOpen: boolean): UseCommandPaletteResult {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CommandResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) { setQuery(''); setSelectedIndex(0); setResults([...NAV_ITEMS, ...ACTION_ITEMS]); }
  }, [isOpen]);

  const search = useCallback(async (q: string) => {
    if (!businessId || !q.trim()) {
      setResults([...NAV_ITEMS, ...ACTION_ITEMS]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const term = `%${q.trim()}%`;

    try {
      const [usersRes, invoicesRes, jobsRes, bookingsRes] = await Promise.all([
        supabase.from('users')
          .select('id, first_name, last_name, role, employment_type')
          .eq('business_id', businessId)
          .or(`first_name.ilike.${term},last_name.ilike.${term},email.ilike.${term}`)
          .limit(3),
        supabase.from('invoices')
          .select('id, invoice_number, client_name, total, status')
          .eq('business_id', businessId)
          .or(`invoice_number.ilike.${term},client_name.ilike.${term}`)
          .limit(3),
        supabase.from('work_orders')
          .select('id, title, client_name, status')
          .eq('business_id', businessId)
          .or(`title.ilike.${term},client_name.ilike.${term}`)
          .limit(3),
        supabase.from('bookings')
          .select('id, client_name, date, start_time, status')
          .eq('business_id', businessId)
          .ilike('client_name', term)
          .limit(3),
      ]);

      const combined: CommandResult[] = [];

      // Team members
      for (const u of usersRes.data ?? []) {
        combined.push({
          id: `user-${u.id}`, type: 'team_member',
          title: `${u.first_name} ${u.last_name}`,
          subtitle: `${u.role} · ${u.employment_type}`,
          icon: '\uD83D\uDC64', route: '/team',
        });
      }

      // Invoices
      for (const i of invoicesRes.data ?? []) {
        combined.push({
          id: `inv-${i.id}`, type: 'invoice',
          title: i.invoice_number,
          subtitle: `${i.client_name ?? 'Unknown'} · $${i.total} · ${i.status}`,
          icon: '\uD83D\uDCC4', route: `/money/invoices?invoice_id=${i.id}`,
        });
      }

      // Jobs
      for (const j of jobsRes.data ?? []) {
        combined.push({
          id: `job-${j.id}`, type: 'job',
          title: j.title,
          subtitle: `${j.client_name ?? ''} · ${j.status}`.replace(/^ · /, ''),
          icon: '\uD83D\uDCCB', route: '/schedule',
        });
      }

      // Bookings
      for (const b of bookingsRes.data ?? []) {
        combined.push({
          id: `bk-${b.id}`, type: 'booking',
          title: b.client_name ?? 'Booking',
          subtitle: `${b.date} · ${b.start_time} · ${b.status}`,
          icon: '\uD83D\uDCC5', route: '/schedule',
        });
      }

      // Settings search — match against title, description, and keywords
      const ql = q.toLowerCase();
      const settingsMatches = SETTINGS_SEARCH_INDEX.filter(entry =>
        entry.title.toLowerCase().includes(ql)
        || entry.description.toLowerCase().includes(ql)
        || entry.keywords.some(kw => kw.toLowerCase().includes(ql)),
      ).slice(0, 5).map(entry => ({
        id: entry.id,
        type: 'settings' as const,
        title: entry.title,
        subtitle: entry.description,
        icon: entry.icon,
        route: entry.route,
      }));
      combined.push(...settingsMatches);

      // Static matches
      const navMatches = NAV_ITEMS.filter(n => n.title.toLowerCase().includes(ql));
      const actMatches = ACTION_ITEMS.filter(a => a.title.toLowerCase().includes(ql));

      combined.push(...navMatches, ...actMatches);
      setResults(combined);
      setSelectedIndex(0);
    } catch {
      // Keep existing results on error
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([...NAV_ITEMS, ...ACTION_ITEMS]);
      setSelectedIndex(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => search(query), 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, isOpen, search]);

  return { query, setQuery, results, loading, selectedIndex, setSelectedIndex };
}
